import { useCallback, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useKeyPressWithReset } from "../../../../../../hooks/useKeyPressWithReset";
import { useReferenceAircraftState } from "../../../RampEditor/stores/useReferenceAircraftState";
import { rotatePoints, scalePolygon } from "../math";
import { Shape } from "../RampCanvasV2";
import { updateAtId } from "../utils";

type Props = {
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  activeShapes: string[];
  setActiveShapes: React.Dispatch<React.SetStateAction<string[]>>;
  pixelsToFeetRatio: number;
  getActiveShapes: () => Shape[];
  undo: () => void;
  duplicateShapes: () => void;
  setSelectedTool: (tool: string) => void;
  copyShapes: () => void;
  pasteShapes: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
};
export const useCanvasHotkeys = (props: Props) => {
  const {
    shapes,
    setShapes,
    activeShapes,
    pixelsToFeetRatio,
    undo,
    duplicateShapes,
    copyShapes,
    pasteShapes,
    zoomIn,
    zoomOut,
  } = props;
  /**
   * Rotate the shape we're hover over when shift + <arrow> is pressed
   */
  const isShiftPressed = useKeyPressWithReset("Shift");
  const isRightArrowPressed = useKeyPressWithReset("ArrowRight");
  const isLeftArrowPressed = useKeyPressWithReset("ArrowLeft");
  const isUpArrowPressed = useKeyPressWithReset("ArrowUp");
  const isDownArrowPressed = useKeyPressWithReset("ArrowDown");

  // Extract movement logic into a memoized callback
  const performMovement = useCallback(() => {
    if (activeShapes.length === 0) return;

    // Rotation with Shift + Left/Right
    if (isShiftPressed && (isRightArrowPressed || isLeftArrowPressed)) {
      setShapes((currentShapes) => {
        const selectedShapes = currentShapes.filter((s) =>
          activeShapes.includes(s.id)
        );

        // T-shape snapping logic
        if (
          (selectedShapes.every((shape) => shape.type === "Tie Down T") ||
            selectedShapes.every((shape) => shape.type === "Double T")) &&
          !selectedShapes
            .map((shape) =>
              Math.round(
                100 *
                  Math.atan2(
                    shape.points[1].y - shape.points[0].y,
                    shape.points[1].x - shape.points[0].x
                  )
              )
            )
            .every((val, i, arr) => val === arr[0])
        ) {
          const referenceAngle = Math.atan2(
            selectedShapes[0].points[1].y - selectedShapes[0].points[0].y,
            selectedShapes[0].points[1].x - selectedShapes[0].points[0].x
          );

          let updatedShapes = [...currentShapes];
          selectedShapes.forEach((shape) => {
            const currentAngle = Math.atan2(
              shape.points[1].y - shape.points[0].y,
              shape.points[1].x - shape.points[0].x
            );
            const angleDifference = referenceAngle - currentAngle;
            const rotatedPoints = rotatePoints(
              shape.points,
              (angleDifference * 180) / Math.PI
            );
            updatedShapes = updateAtId(updatedShapes, {
              ...shape,
              points: rotatedPoints,
            });
          });
          return updatedShapes;
        }

        // Regular rotation
        let updatedShapes = [...currentShapes];
        selectedShapes.forEach((shape) => {
          updatedShapes = updateAtId(updatedShapes, {
            ...shape,
            points: rotatePoints(
              shape.points,
              isRightArrowPressed ? 1 : -1
            ),
          });
        });
        return updatedShapes;
      });
      return;
    }

    // Scaling with Shift + Up/Down
    if (isShiftPressed && (isUpArrowPressed || isDownArrowPressed)) {
      setShapes((currentShapes) => {
        const selectedShapes = currentShapes.filter((s) =>
          activeShapes.includes(s.id)
        );

        let updatedShapes = [...currentShapes];
        selectedShapes.forEach((shape) => {
          updatedShapes = updateAtId(updatedShapes, {
            ...shape,
            points: scalePolygon(shape.points, isUpArrowPressed ? 1.01 : 0.99),
          });
        });
        return updatedShapes;
      });
      return;
    }

    // Regular movement (no Shift)
    if (!isShiftPressed) {
      setShapes((currentShapes) => {
        const relevantShapes = currentShapes.filter((s) =>
          activeShapes.includes(s.id)
        );

        let updatedShapes = [...currentShapes];
        relevantShapes.forEach((shape) => {
          const dx = isRightArrowPressed ? 1 : isLeftArrowPressed ? -1 : 0;
          const dy = isDownArrowPressed ? 1 : isUpArrowPressed ? -1 : 0;
          updatedShapes = updateAtId(updatedShapes, {
            ...shape,
            points: shape.points.map((point) => ({
              x: point.x + dx * pixelsToFeetRatio,
              y: point.y + dy * pixelsToFeetRatio,
            })),
          });
        });
        return updatedShapes;
      });
    }
  }, [
    setShapes,
    activeShapes,
    pixelsToFeetRatio,
    isShiftPressed,
    isRightArrowPressed,
    isLeftArrowPressed,
    isUpArrowPressed,
    isDownArrowPressed,
  ]);

  useEffect(() => {
    // Don't set up interval if no keys are pressed
    const anyKeyPressed =
      isLeftArrowPressed ||
      isRightArrowPressed ||
      isDownArrowPressed ||
      isUpArrowPressed;

    if (!anyKeyPressed || activeShapes.length === 0) {
      return;
    }

    // Don't process arrow keys when user is typing in an input/textarea
    const activeElement = document.activeElement;
    const isTyping =
      activeElement?.tagName === "INPUT" ||
      activeElement?.tagName === "TEXTAREA" ||
      activeElement?.getAttribute("contenteditable") === "true";

    if (isTyping) {
      return;
    }

    // Run immediately on first key press
    performMovement();

    // Set up interval for continuous movement while key is held (50ms = 20 updates/sec)
    const interval = setInterval(performMovement, 50);

    // Cleanup interval when keys are released or component unmounts
    return () => clearInterval(interval);
  }, [
    performMovement,
    activeShapes,
    isLeftArrowPressed,
    isRightArrowPressed,
    isDownArrowPressed,
    isUpArrowPressed,
  ]);

  useHotkeys(
    "ctrl+d",
    () => {
      duplicateShapes();
    },
    [activeShapes, shapes]
  );

  useHotkeys(
    ["meta+z", "ctrl+z"],
    () => {
      undo();
    },
    [undo]
  );

  // Copy shapes
  useHotkeys(
    ["meta+c", "ctrl+c"],
    () => {
      copyShapes();
    },
    [copyShapes]
  );

  // Paste shapes
  useHotkeys(
    ["meta+v", "ctrl+v"],
    () => {
      pasteShapes();
    },
    [pasteShapes]
  );

  // Reference aircraft hotkeys
  const {
    isSelected: referenceIsSelected,
    rotate: rotateReferenceAircraft,
    clear: clearReferenceAircraft,
  } = useReferenceAircraftState();

  // R key: rotate reference aircraft clockwise by 15 degrees
  useHotkeys(
    "r",
    () => {
      if (referenceIsSelected) {
        rotateReferenceAircraft((15 * Math.PI) / 180); // 15 degrees in radians
      }
    },
    [referenceIsSelected, rotateReferenceAircraft]
  );

  // Shift+R: rotate reference aircraft counter-clockwise by 15 degrees
  useHotkeys(
    "shift+r",
    () => {
      if (referenceIsSelected) {
        rotateReferenceAircraft((-15 * Math.PI) / 180); // -15 degrees in radians
      }
    },
    [referenceIsSelected, rotateReferenceAircraft]
  );

  // Delete/Backspace: delete selected shapes OR clear reference aircraft
  useHotkeys(
    ["delete", "backspace"],
    () => {
      // If reference aircraft is selected, clear it
      if (referenceIsSelected) {
        clearReferenceAircraft();
        return;
      }
      // Otherwise delete selected shapes
      if (activeShapes.length > 0) {
        setShapes((currentShapes) =>
          currentShapes.filter((s) => !activeShapes.includes(s.id))
        );
        props.setActiveShapes([]);
      }
    },
    [referenceIsSelected, clearReferenceAircraft, activeShapes, setShapes]
  );

  // Cmd+A / Ctrl+A: select all shapes
  useHotkeys(
    ["meta+a", "ctrl+a"],
    (e) => {
      e.preventDefault();
      props.setActiveShapes(shapes.map((s) => s.id));
    },
    [shapes]
  );

  // Escape: deselect all shapes
  useHotkeys(
    "escape",
    () => {
      props.setActiveShapes([]);
    },
    []
  );

  // Tool selection hotkeys
  useHotkeys("v", () => props.setSelectedTool("Select"), []);
  useHotkeys("a", () => props.setSelectedTool("Area"), []);
  useHotkeys("z", () => props.setSelectedTool("Zone"), []);
  useHotkeys("l", () => props.setSelectedTool("Line"), []);
  useHotkeys("s", () => props.setSelectedTool("Spot"), []);
  useHotkeys("t", () => props.setSelectedTool("Text"), []);

  // Zoom hotkeys (Cmd/Ctrl + 1/2)
  useHotkeys(
    ["meta+2", "ctrl+2"],
    (e) => {
      e.preventDefault();
      zoomIn();
    },
    [zoomIn]
  );

  useHotkeys(
    ["meta+1", "ctrl+1"],
    (e) => {
      e.preventDefault();
      zoomOut();
    },
    [zoomOut]
  );
};
